// src/components/pass/PassGateDialog.tsx
//
// The wall. Mounted once in the layout; opens whenever any request comes
// back 402 PASS_REQUIRED. One sentence about what ran out, one about what
// the pass does, two buttons. It never blames, never counts down, never
// stacks with a toast — call sites stay silent on this code.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PASS_REQUIRED_EVENT, type PassRequired } from "@/lib/passGate";
import { track, EVENTS } from "@/lib/analytics";
import { usePassPricing } from "@/hooks/usePass";

const COPY: Record<PassRequired["feature"], { title: string; body: string }> = {
  mocks: {
    title: "That was your third free mock.",
    body: "The pass opens every mock, every year, until 1 March 2027.",
  },
  solutions: {
    title: "The first five solutions were on us.",
    body: "Every solution, in every mock and practice set, with the pass.",
  },
  contests: {
    title: "One contest is free to host.",
    body: "Host as many as you like with the pass. Joining is always free.",
  },
};

export function PassGateDialog() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<PassRequired | null>(null);
  const { price } = usePassPricing();

  useEffect(() => {
    const onGate = (e: Event) => {
      const detail = (e as CustomEvent<PassRequired>).detail;
      setGate(detail);
      track(EVENTS.SOLUTION_LOCKED_SEEN, { feature: detail.feature, surface: "dialog" });
    };
    window.addEventListener(PASS_REQUIRED_EVENT, onGate);
    return () => window.removeEventListener(PASS_REQUIRED_EVENT, onGate);
  }, []);

  const copy = gate ? COPY[gate.feature] : null;

  return (
    <Dialog open={!!gate} onOpenChange={(open) => !open && setGate(null)}>
      <DialogContent className="max-w-[400px] gap-0 rounded-2xl p-0">
        <div className="flex flex-col gap-2 px-6 pt-7">
          <span className="eyebrow">GATE 2027 Pass</span>
          <DialogTitle className="text-[20px] font-semibold leading-snug tracking-[-0.02em]">
            {copy?.title}
          </DialogTitle>
          <DialogDescription className="text-[14px] leading-relaxed text-muted-foreground">
            {copy?.body}
          </DialogDescription>
        </div>

        <div className="flex flex-col gap-2 px-6 pb-6 pt-6">
          <button
            type="button"
            onClick={() => {
              track(EVENTS.PASS_CTA_CLICKED, { surface: "dialog", feature: gate?.feature });
              setGate(null);
              navigate("/pricing");
            }}
            className="pass-sheen flex items-center justify-between rounded-xl px-4 py-3 text-[14px] font-semibold transition-transform hover:scale-[1.01]"
          >
            Get the pass
            <span className="flex items-center gap-2 font-mono text-[12px] opacity-80">
              ₹{price}
              <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </button>
          <button
            type="button"
            onClick={() => setGate(null)}
            className="rounded-xl px-4 py-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            Not now
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
